// Email Service using Zoho API
// Zoho provides 25 emails/day for free tier

class EmailService {
  constructor() {
    this.baseURL = 'https://api.zoho.com/crm/v2';
    this.emailURL = 'https://mail.zoho.com/api/accounts';
    this.accessToken = null;
    this.refreshToken = null;
    this.clientId = process.env.REACT_APP_ZOHO_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_ZOHO_CLIENT_SECRET;
    this.refreshTokenValue = process.env.REACT_APP_ZOHO_REFRESH_TOKEN;
    this.fromEmail = process.env.REACT_APP_ZOHO_FROM_EMAIL;
  }

  // Initialize the service
  async initialize() {
    try {
      if (!this.clientId || !this.clientSecret || !this.refreshTokenValue) {
        throw new Error('Zoho credentials not configured. Please set environment variables.');
      }
      
      await this.refreshAccessToken();
      console.log('✅ Email service initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize email service:', error);
      return false;
    }
  }

  // Refresh access token using refresh token
  async refreshAccessToken() {
    try {
      const response = await fetch('https://accounts.zoho.com/oauth/v2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          refresh_token: this.refreshTokenValue,
          client_id: this.clientId,
          client_secret: this.clientSecret,
          grant_type: 'refresh_token',
        }),
      });

      const data = await response.json();
      
      if (data.access_token) {
        this.accessToken = data.access_token;
        console.log('✅ Access token refreshed successfully');
        return true;
      } else {
        throw new Error('Failed to refresh access token');
      }
    } catch (error) {
      console.error('❌ Error refreshing access token:', error);
      throw error;
    }
  }

  // Send email using Zoho Mail API
  async sendEmail(to, subject, body, options = {}) {
    try {
      if (!this.accessToken) {
        await this.refreshAccessToken();
      }

      const emailData = {
        fromAddress: this.fromEmail,
        toAddress: Array.isArray(to) ? to.join(',') : to,
        subject: subject,
        content: body,
        mailFormat: options.html ? 'html' : 'plain',
        ...options
      };

      const response = await fetch(`${this.emailURL}/self/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Zoho-oauthtoken ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      const result = await response.json();
      
      if (response.ok) {
        console.log('✅ Email sent successfully:', result);
        return {
          success: true,
          messageId: result.data?.messageId,
          data: result
        };
      } else {
        throw new Error(`Failed to send email: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error sending email:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Send welcome email to new users
  async sendWelcomeEmail(userEmail, userName) {
    const subject = 'Welcome to College Avenue! 🎓';
    const body = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; color: white;">
          <h1 style="margin: 0; font-size: 28px;">Welcome to College Avenue!</h1>
          <p style="margin: 10px 0 0 0; font-size: 16px;">Your college admission journey starts here</p>
        </div>
        
        <div style="padding: 30px; background: #f8f9fa;">
          <h2 style="color: #333; margin-bottom: 20px;">Hi ${userName || 'there'}! 👋</h2>
          
          <p style="color: #555; line-height: 1.6; margin-bottom: 20px;">
            Welcome to College Avenue! We're excited to have you join our community of students 
            making informed decisions about their college admissions.
          </p>
          
          <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
            <h3 style="color: #333; margin-top: 0;">What you can do now:</h3>
            <ul style="color: #555; line-height: 1.8;">
              <li>🎯 Try our AI College Predictor</li>
              <li>📊 Explore real-time cutoff data</li>
              <li>👥 Join student discussions</li>
              <li>📅 Track important deadlines</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${process.env.REACT_APP_BASE_URL}/dashboard" 
               style="background: #667eea; color: white; padding: 12px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">
              Go to Dashboard
            </a>
          </div>
          
          <p style="color: #777; font-size: 14px; margin-top: 30px;">
            If you have any questions, feel free to reach out to our support team.
          </p>
        </div>
        
        <div style="background: #333; color: white; padding: 20px; text-align: center; font-size: 12px;">
          <p>© 2024 College Avenue. All rights reserved.</p>
        </div>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, body, { html: true });
  }

  // Send password reset email
  async sendPasswordResetEmail(userEmail, resetToken) {
    const resetLink = `${process.env.REACT_APP_BASE_URL}/reset-password?token=${resetToken}`;
    const subject = 'Reset Your College Avenue Password';
    const body = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; color: white;">
          <h1 style="margin: 0; font-size: 28px;">Password Reset Request</h1>
        </div>
        
        <div style="padding: 30px; background: #f8f9fa;">
          <h2 style="color: #333; margin-bottom: 20px;">Hello! 👋</h2>
          
          <p style="color: #555; line-height: 1.6; margin-bottom: 20px;">
            We received a request to reset your password for your College Avenue account. 
            If you didn't make this request, you can safely ignore this email.
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${resetLink}" 
               style="background: #667eea; color: white; padding: 12px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">
              Reset Password
            </a>
          </div>
          
          <p style="color: #777; font-size: 14px;">
            This link will expire in 1 hour for security reasons.
          </p>
          
          <p style="color: #777; font-size: 14px; margin-top: 20px;">
            If the button doesn't work, copy and paste this link into your browser:<br>
            <a href="${resetLink}" style="color: #667eea;">${resetLink}</a>
          </p>
        </div>
        
        <div style="background: #333; color: white; padding: 20px; text-align: center; font-size: 12px;">
          <p>© 2024 College Avenue. All rights reserved.</p>
        </div>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, body, { html: true });
  }

  // Send email verification
  async sendEmailVerification(userEmail, verificationToken) {
    const verificationLink = `${process.env.REACT_APP_BASE_URL}/verify-email?token=${verificationToken}`;
    const subject = 'Verify Your College Avenue Email';
    const body = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; color: white;">
          <h1 style="margin: 0; font-size: 28px;">Verify Your Email</h1>
        </div>
        
        <div style="padding: 30px; background: #f8f9fa;">
          <h2 style="color: #333; margin-bottom: 20px;">Almost there! 🎉</h2>
          
          <p style="color: #555; line-height: 1.6; margin-bottom: 20px;">
            Thanks for signing up for College Avenue! To complete your registration, 
            please verify your email address by clicking the button below.
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${verificationLink}" 
               style="background: #667eea; color: white; padding: 12px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">
              Verify Email
            </a>
          </div>
          
          <p style="color: #777; font-size: 14px;">
            This link will expire in 24 hours.
          </p>
        </div>
        
        <div style="background: #333; color: white; padding: 20px; text-align: center; font-size: 12px;">
          <p>© 2024 College Avenue. All rights reserved.</p>
        </div>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, body, { html: true });
  }

  // Send notification email
  async sendNotificationEmail(userEmail, title, message, actionLink = null, actionText = null) {
    const subject = title;
    const body = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; color: white;">
          <h1 style="margin: 0; font-size: 28px;">${title}</h1>
        </div>
        
        <div style="padding: 30px; background: #f8f9fa;">
          <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
            <p style="color: #555; line-height: 1.6; margin: 0;">${message}</p>
          </div>
          
          ${actionLink && actionText ? `
            <div style="text-align: center; margin: 30px 0;">
              <a href="${actionLink}" 
                 style="background: #667eea; color: white; padding: 12px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">
                ${actionText}
              </a>
            </div>
          ` : ''}
        </div>
        
        <div style="background: #333; color: white; padding: 20px; text-align: center; font-size: 12px;">
          <p>© 2024 College Avenue. All rights reserved.</p>
        </div>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, body, { html: true });
  }

  // Check email sending quota
  async checkQuota() {
    try {
      if (!this.accessToken) {
        await this.refreshAccessToken();
      }

      const response = await fetch(`${this.emailURL}/self/quota`, {
        headers: {
          'Authorization': `Zoho-oauthtoken ${this.accessToken}`,
        },
      });

      const result = await response.json();
      
      if (response.ok) {
        return {
          success: true,
          quota: result.data,
          remaining: result.data?.dailyLimit - result.data?.dailyUsed
        };
      } else {
        throw new Error('Failed to check quota');
      }
    } catch (error) {
      console.error('❌ Error checking quota:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const emailService = new EmailService();

export default emailService;

